// Ported from genieos/web/src/components/historian/ChartErrorBoundary.tsx @ 84923d036
/**
 * ChartErrorBoundary - Error boundary for historian chart component
 *
 * Catches rendering errors in the chart and displays a user-friendly
 * error message with retry capability. Prevents chart crashes from
 * breaking the entire historian page.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button, Card } from '@/components/gamma';

interface ChartErrorBoundaryProps {
  children: ReactNode;
  onReset?: () => void;
  height?: number;
}

interface ChartErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ChartErrorBoundary extends Component<
  ChartErrorBoundaryProps,
  ChartErrorBoundaryState
> {
  public state: ChartErrorBoundaryState = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): ChartErrorBoundaryState {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Chart error:', error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  public render() {
    if (this.state.hasError) {
      const { height = 400 } = this.props;

      return (
        <Card
          variant="default"
          className="flex items-center justify-center"
          style={{ height }}
        >
          <div className="flex flex-col items-center gap-3 text-center p-6 max-w-md">
            <AlertTriangle className="w-10 h-10 text-ems-red" />
            <h3 className="text-lg font-medium text-ems-text-primary">
              Chart Rendering Error
            </h3>
            <p className="text-ems-text-secondary text-sm">
              An error occurred while rendering the chart. This might be due to
              invalid data or a temporary issue.
            </p>
            {this.state.error?.message && (
              <p className="text-ems-text-secondary/60 text-xs font-mono bg-black/20 px-3 py-2 rounded">
                {this.state.error.message}
              </p>
            )}
            <Button
              variant="primary"
              size="sm"
              onClick={this.handleReset}
              leftIcon={<RefreshCw className="w-4 h-4" />}
              className="mt-2"
            >
              Try Again
            </Button>
          </div>
        </Card>
      );
    }

    return this.props.children;
  }
}

export default ChartErrorBoundary;
